import React, { type FC, type PropsWithChildren } from 'react'
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, type SxProps, type Theme } from '@mui/material'

import { type Certificate, ConnectwareError, getCertificateTitle, Translation } from '../../../domain'

import { useAsyncCallback } from '../Callback'
import { Button } from '../common'
import { ErrorMessage } from '../ErrorMessage'
import { FormattedTranslation } from '../Internationalization'

const errorMessageStyle: SxProps<Theme> = { width: '100%' }
const dialogTitleStyle: SxProps<Theme> = { mb: 0 }

export type CertificateDialogProps = Readonly<{
    certificate: Certificate
    confirmation?: Translation
    onClose: VoidFunction
    confirmable?: boolean
    onConfirm?: () => Promise<void>
}>

export const CertificateDialog: FC<PropsWithChildren<CertificateDialogProps>> = ({ certificate, confirmation, confirmable, onConfirm, onClose, children }) => {
    const title = getCertificateTitle(certificate)

    const [confirm, isLoading, result] = useAsyncCallback(async () => {
        await onConfirm?.()
        onClose()
    }, [onConfirm])

    return (
        <Dialog maxWidth="md" fullWidth open onClose={onClose}>
            <DialogTitle sx={dialogTitleStyle} variant="body1" paragraph>
                <FormattedTranslation id={Translation.CERTIFICATE_VIEWER_TITLE} values={{ title }} />
            </DialogTitle>
            <DialogContent dividers>{children}</DialogContent>
            <DialogActions>
                {isLoading && <CircularProgress size={20} />}
                {ConnectwareError.is(result) && (
                    <ErrorMessage sx={errorMessageStyle} data-testid="certificate-deletion-error" error={result} titleVariant="body2" extras="popover" />
                )}
                <Button variant="secondary" onClick={onClose} data-testid="certificate-cancel-button">
                    <FormattedTranslation id={Translation.CLOSE} />
                </Button>
                {confirmation && onConfirm && (
                    <Button data-testid="certificate-confirmation-button" variant="danger" disabled={isLoading || confirmable === false} onClick={confirm}>
                        <FormattedTranslation id={confirmation} />
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}
